import type { BlogPost } from "./content-types";

/**
 * The 5 new educational blog articles (the 6th, "comment-numeriser-recus-
 * application-mobile", already exists as the BlogScan page). Each answers one
 * precise question, then points to its main pillar page.
 */

const FR = {
  receipts: "/application-recus",
  returns: "/application-retours-magasin",
  warranties: "/application-garanties",
  family: "/application-recus-famille",
};
const EN = {
  receipts: "/en/receipt-app",
  returns: "/en/returns-app",
  warranties: "/en/warranty-tracker-app",
  family: "/en/family-receipt-app",
};

export const posts: BlogPost[] = [
  /* ---------------------------- faded receipt ---------------------------- */
  {
    slug: "recu-efface-quoi-faire",
    frPath: "/blog/recu-efface-quoi-faire",
    enPath: "/en/blog/faded-receipt-what-to-do",
    image: "https://images.unsplash.com/photo-1554224154-26032ffc0d07?w=1200&q=80",
    imageAltFr: "Reçu papier thermique effacé",
    imageAltEn: "Faded thermal paper receipt",
    datePublished: "2026-06-10T08:00:00-04:00",
    dateModified: "2026-06-10T08:00:00-04:00",
    fr: {
      metaTitle: "Reçu effacé : quoi faire avec un reçu illisible? | ReFound",
      metaDescription:
        "Un reçu papier effacé peut compliquer un retour ou une garantie. Voici quoi faire et comment numériser vos reçus à temps pour garder une preuve lisible.",
      keywords:
        "reçu effacé, reçu illisible, papier thermique effacé, sauver un reçu, preuve d'achat, numériser un reçu, ReFound",
      category: "Guide",
      date: "10 juin 2026",
      readingTime: "5 min de lecture",
      breadcrumb: "Reçu effacé : quoi faire?",
      eyebrow: "Guide",
      h1: "Reçu effacé : quoi faire avec un reçu papier devenu illisible?",
      intro: [
        "Réponse courte : agissez vite. Si le reçu est encore partiellement lisible, prenez-le en photo ou scannez-le immédiatement pour garder une preuve d'achat. Pour les prochains achats, numériser vos reçus dès l'achat évite ce problème, car le papier thermique s'efface avec le temps.",
      ],
      sections: [
        {
          heading: "Pourquoi les reçus s'effacent",
          body: [
            "La plupart des reçus de magasin sont imprimés sur du papier thermique. La chaleur, la lumière et le temps font pâlir l'encre. Après quelques semaines ou quelques mois, le reçu peut devenir illisible, juste au moment où vous en auriez besoin pour un retour ou une garantie.",
          ],
        },
        {
          heading: "Que faire si votre reçu est déjà effacé",
          bullets: [
            "Photographiez ou scannez ce qui reste lisible, tout de suite.",
            "Cherchez une confirmation par courriel si l'achat était en ligne ou avec un compte de fidélité.",
            "Notez la date, le marchand et le mode de paiement pendant que vous vous en souvenez.",
            "Contactez le marchand : certains peuvent retrouver une transaction, selon leur politique.",
          ],
        },
        {
          heading: "La meilleure solution : numériser vos reçus à temps",
          body: [
            "Le plus simple reste de garder une copie numérique avant que le papier s'efface. Une [application de reçus](/application-recus) comme ReFound conserve une copie lisible et organisée de vos reçus dès l'achat.",
            "Voyez aussi [comment numériser des reçus avec une application mobile](/blog/comment-numeriser-recus-application-mobile).",
          ],
        },
        {
          heading: "Garder la preuve pour un retour ou une garantie",
          body: [
            "Une copie numérique peut aider à présenter une preuve d'achat même si le papier original a disparu, selon la politique du marchand. ReFound peut aussi associer une [garantie du manufacturier](/application-garanties) quand l'information est disponible.",
          ],
        },
      ],
      faqTitle: "Questions fréquentes",
      faq: [
        { q: "Peut-on faire un retour avec un reçu effacé?", a: "Cela dépend de la politique du marchand. Une copie numérique lisible peut aider à présenter une preuve d'achat, mais l'acceptation reste à la discrétion du marchand." },
        { q: "Comment empêcher un reçu de s'effacer?", a: "Évitez la chaleur et la lumière, mais le plus sûr est de numériser le reçu dès l'achat pour en garder une copie lisible." },
        { q: "Est-ce qu'une photo de reçu suffit?", a: "Une photo aide, mais elle se perd vite dans la galerie. Une application de reçus garde la copie lisible et organisée pour la retrouver au besoin." },
        { q: "ReFound peut-il aider avec un vieux reçu?", a: "Si vous l'avez numérisé à temps, ReFound en conserve une copie lisible. Sinon, il aide surtout pour vos prochains achats." },
      ],
      ctaTitle: "Ne perdez plus une preuve d'achat",
      ctaBody: "ReFound transforme vos reçus en preuves d'achat intelligentes, lisibles avant que le papier ne s'efface.",
      relatedTitle: "Pour aller plus loin",
      related: [
        { label: "Application de reçus", href: FR.receipts, desc: "Scanner, enrichir et retrouver vos reçus." },
        { label: "Application de garanties", href: FR.warranties, desc: "Conserver vos preuves d'achat." },
        { label: "Comment numériser ses reçus", href: "/blog/comment-numeriser-recus-application-mobile", desc: "Le guide complet." },
      ],
    },
    en: {
      metaTitle: "Faded Receipt: What to Do With an Unreadable Receipt | ReFound",
      metaDescription:
        "A faded paper receipt can make returns and warranties harder. Here is what to do, and how to scan your receipts in time to keep readable proof of purchase.",
      keywords:
        "faded receipt, unreadable receipt, faded thermal paper, save a receipt, proof of purchase, scan a receipt, ReFound",
      category: "Guide",
      date: "June 10, 2026",
      readingTime: "5 min read",
      breadcrumb: "Faded receipt: what to do?",
      eyebrow: "Guide",
      h1: "Faded receipt: what to do with an unreadable paper receipt?",
      intro: [
        "Short answer: act fast. If the receipt is still partly readable, take a photo or scan it right away to keep proof of purchase. For future purchases, scanning receipts at checkout avoids this problem, because thermal paper fades over time.",
      ],
      sections: [
        {
          heading: "Why receipts fade",
          body: [
            "Most store receipts are printed on thermal paper. Heat, light and time cause the ink to fade. After a few weeks or months, the receipt can become unreadable, right when you need it for a return or warranty.",
          ],
        },
        {
          heading: "What to do if your receipt has already faded",
          bullets: [
            "Photograph or scan whatever is still readable, right away.",
            "Look for an email confirmation if the purchase was online or with a loyalty account.",
            "Write down the date, merchant and payment method while you remember.",
            "Contact the merchant: some can look up a transaction, based on their policy.",
          ],
        },
        {
          heading: "The best fix: scan your receipts in time",
          body: [
            "The simplest approach is to keep a digital copy before the paper fades. A [receipt app](/en/receipt-app) like ReFound keeps a readable, organized copy of your receipts from the moment you buy.",
            "See also [how to scan receipts with a mobile app](/en/blog/comment-numeriser-recus-application-mobile).",
          ],
        },
        {
          heading: "Keep proof for a return or warranty",
          body: [
            "A digital copy can help present proof of purchase even if the original paper is gone, based on merchant policy. ReFound can also attach a [manufacturer warranty](/en/warranty-tracker-app) when the information is available.",
          ],
        },
      ],
      faqTitle: "Frequently asked questions",
      faq: [
        { q: "Can you return an item with a faded receipt?", a: "It depends on the merchant's policy. A readable digital copy can help present proof of purchase, but acceptance is up to the merchant." },
        { q: "How do you stop a receipt from fading?", a: "Avoid heat and light, but the safest option is to scan the receipt at purchase to keep a readable copy." },
        { q: "Is a photo of a receipt enough?", a: "A photo helps, but it gets lost in your camera roll. A receipt app keeps the readable copy organized so you can find it when needed." },
        { q: "Can ReFound help with an old receipt?", a: "If you scanned it in time, ReFound keeps a readable copy. Otherwise it mainly helps with your future purchases." },
      ],
      ctaTitle: "Never lose proof of purchase again",
      ctaBody: "ReFound turns receipts into smart proof of purchase, readable before the paper fades.",
      relatedTitle: "Go further",
      related: [
        { label: "Receipt app", href: EN.receipts, desc: "Scan, enrich and find your receipts." },
        { label: "Warranty tracker app", href: EN.warranties, desc: "Keep your proof of purchase." },
        { label: "How to scan your receipts", href: "/en/blog/comment-numeriser-recus-application-mobile", desc: "The full guide." },
      ],
    },
  },

  /* ------------------------ keep receipts warranties ------------------------ */
  {
    slug: "comment-conserver-recus-garanties",
    frPath: "/blog/comment-conserver-recus-garanties",
    enPath: "/en/blog/how-to-keep-receipts-for-warranties",
    image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&q=80",
    imageAltFr: "Reçus conservés pour les garanties",
    imageAltEn: "Receipts kept for warranties",
    datePublished: "2026-06-11T08:00:00-04:00",
    dateModified: "2026-06-11T08:00:00-04:00",
    fr: {
      metaTitle: "Comment conserver ses reçus pour les garanties | ReFound",
      metaDescription:
        "Pour une réclamation de garantie, la preuve d'achat est souvent requise. Voici comment conserver vos reçus et retrouver le bon au bon moment.",
      keywords:
        "conserver reçus garanties, preuve d'achat garantie, suivi de garantie, garder un reçu, réclamation garantie, ReFound",
      category: "Guide",
      date: "11 juin 2026",
      readingTime: "6 min de lecture",
      breadcrumb: "Conserver ses reçus pour les garanties",
      eyebrow: "Guide",
      h1: "Comment conserver ses reçus pour les garanties",
      intro: [
        "Réponse courte : gardez une copie numérique de la preuve d'achat au même endroit que les détails du produit, dès l'achat. La plupart des réclamations de garantie exigent le reçu, et le retrouver des mois plus tard est souvent le vrai défi.",
      ],
      sections: [
        {
          heading: "Pourquoi la preuve d'achat compte pour une garantie",
          body: [
            "Un fabricant ou un marchand demande presque toujours la preuve d'achat pour valider une garantie : elle confirme la date et le lieu de l'achat. Sans reçu lisible, une réclamation devient beaucoup plus difficile.",
          ],
        },
        {
          heading: "Les meilleures façons de conserver vos reçus",
          sub: [
            { heading: "Garder le papier (fragile)", body: ["Le tiroir à reçus fonctionne, mais le papier thermique s'efface et se perd facilement."] },
            { heading: "Photo dans le téléphone (désorganisé)", body: ["Une photo aide, mais elle se noie dans la galerie et devient difficile à retrouver."] },
            { heading: "Application de reçus (recommandé)", body: ["Une [application de garanties](/application-garanties) conserve une copie lisible et organisée, et peut associer la garantie du manufacturier quand l'information est disponible."] },
          ],
        },
        {
          heading: "Retrouver le bon reçu au moment d'un problème",
          body: [
            "Quand un appareil brise, la recherche rapide fait toute la différence. ReFound organise vos reçus par marchand, produit et date pour retrouver la preuve d'achat liée au produit.",
          ],
          bullets: [
            "Copie lisible conservée à temps",
            "Garantie du manufacturier associée quand disponible",
            "Date limite de retour estimée quand disponible",
          ],
        },
      ],
      faqTitle: "Questions fréquentes",
      faq: [
        { q: "Combien de temps faut-il garder un reçu pour une garantie?", a: "Au moins pendant la durée de la garantie du produit. Pour les électroménagers et l'électronique, cela peut représenter plusieurs années." },
        { q: "Une copie numérique est-elle acceptée pour une garantie?", a: "Souvent oui, mais cela dépend du marchand ou du fabricant. Garder une copie lisible aide à présenter la preuve d'achat au besoin." },
        { q: "ReFound suit-il les dates de garantie?", a: "ReFound aide à garder les informations utiles au même endroit et peut associer une garantie quand l'information est disponible, selon le produit et le marchand." },
        { q: "Est-ce que tous les produits ont une garantie enrichie?", a: "Non. Les informations de garantie sont ajoutées seulement quand elles sont disponibles. ReFound conserve tout de même votre reçu comme preuve d'achat." },
      ],
      ctaTitle: "Vos garanties commencent par un bon reçu",
      ctaBody: "ReFound aide à conserver vos reçus et vos garanties au même endroit, prêts au moment d'une réclamation.",
      relatedTitle: "Pour aller plus loin",
      related: [
        { label: "Application de garanties", href: FR.warranties, desc: "Conserver vos preuves d'achat." },
        { label: "Application de reçus", href: FR.receipts, desc: "Scanner et enrichir vos reçus." },
        { label: "Reçu effacé : quoi faire?", href: "/blog/recu-efface-quoi-faire", desc: "Avant que le papier disparaisse." },
      ],
    },
    en: {
      metaTitle: "How to Keep Receipts for Warranties | ReFound",
      metaDescription:
        "Warranty claims usually need proof of purchase. Here is how to keep your receipts for warranties and find the right receipt at the right moment.",
      keywords:
        "keep receipts for warranties, proof of purchase warranty, warranty tracker, keep a receipt, warranty claim, ReFound",
      category: "Guide",
      date: "June 11, 2026",
      readingTime: "6 min read",
      breadcrumb: "Keep receipts for warranties",
      eyebrow: "Guide",
      h1: "How to keep receipts for warranties",
      intro: [
        "Short answer: keep a digital copy of the proof of purchase in the same place as the product details, from the moment you buy. Most warranty claims require the receipt, and finding it months later is often the real challenge.",
      ],
      sections: [
        {
          heading: "Why proof of purchase matters for a warranty",
          body: [
            "A manufacturer or merchant almost always asks for proof of purchase to validate a warranty: it confirms the date and place of purchase. Without a readable receipt, a claim becomes much harder.",
          ],
        },
        {
          heading: "The best ways to keep your receipts",
          sub: [
            { heading: "Keep the paper (fragile)", body: ["The receipt drawer works, but thermal paper fades and is easily lost."] },
            { heading: "Photo on your phone (messy)", body: ["A photo helps, but it drowns in your camera roll and becomes hard to find."] },
            { heading: "Receipt app (recommended)", body: ["A [warranty tracker app](/en/warranty-tracker-app) keeps a readable, organized copy and can attach the manufacturer warranty when the information is available."] },
          ],
        },
        {
          heading: "Find the right receipt when a problem happens",
          body: [
            "When a device breaks, fast search makes all the difference. ReFound organizes your receipts by merchant, product and date to find the proof of purchase tied to the product.",
          ],
          bullets: [
            "Readable copy kept in time",
            "Manufacturer warranty attached when available",
            "Estimated return deadline when available",
          ],
        },
      ],
      faqTitle: "Frequently asked questions",
      faq: [
        { q: "How long should you keep a receipt for a warranty?", a: "At least for the length of the product warranty. For appliances and electronics, that can be several years." },
        { q: "Is a digital copy accepted for a warranty?", a: "Often yes, but it depends on the merchant or manufacturer. Keeping a readable copy helps present proof of purchase when needed." },
        { q: "Does ReFound track warranty dates?", a: "ReFound helps keep the useful information in one place and can attach a warranty when the information is available, based on product and merchant." },
        { q: "Do all products get an enriched warranty?", a: "No. Warranty information is added only when available. ReFound still keeps your receipt as proof of purchase." },
      ],
      ctaTitle: "Your warranties start with a good receipt",
      ctaBody: "ReFound helps you keep your receipts and warranties in one place, ready when it is time for a claim.",
      relatedTitle: "Go further",
      related: [
        { label: "Warranty tracker app", href: EN.warranties, desc: "Keep your proof of purchase." },
        { label: "Receipt app", href: EN.receipts, desc: "Scan and enrich your receipts." },
        { label: "Faded receipt: what to do?", href: "/en/blog/faded-receipt-what-to-do", desc: "Before the paper disappears." },
      ],
    },
  },

  /* ------------------------ return with phone ------------------------ */
  {
    slug: "comment-faire-retour-magasin-avec-telephone",
    frPath: "/blog/comment-faire-retour-magasin-avec-telephone",
    enPath: "/en/blog/how-to-return-items-in-store-with-your-phone",
    image: "https://images.unsplash.com/photo-1556740738-b6a63e27c4df?w=1200&q=80",
    imageAltFr: "Faire un retour en magasin avec son téléphone",
    imageAltEn: "Making an in-store return with your phone",
    datePublished: "2026-06-12T08:00:00-04:00",
    dateModified: "2026-06-12T08:00:00-04:00",
    fr: {
      metaTitle: "Comment faire un retour en magasin avec son téléphone | ReFound",
      metaDescription:
        "Retrouver le bon reçu, afficher le code-barres quand disponible et présenter l'image du reçu peut faciliter un retour, selon la politique du marchand.",
      keywords:
        "retour en magasin téléphone, faire un retour avec son téléphone, reçu numérique retour, code-barres du reçu, preuve d'achat, ReFound",
      category: "Guide",
      date: "12 juin 2026",
      readingTime: "6 min de lecture",
      breadcrumb: "Retour en magasin avec son téléphone",
      eyebrow: "Guide",
      h1: "Comment faire un retour en magasin avec son téléphone",
      intro: [
        "Réponse courte : retrouvez le bon reçu sur votre téléphone, affichez le code-barres au comptoir s'il est détecté, puis présentez l'image du reçu. Cela peut faciliter le retour, mais l'acceptation dépend toujours de la politique du marchand.",
      ],
      sections: [
        {
          heading: "Étape 1 — Retrouver le bon reçu",
          body: [
            "Avant d'aller au magasin, retrouvez le reçu du produit. Avec une [application de retours](/application-retours-magasin), une recherche rapide par marchand, produit ou date évite de fouiller.",
          ],
        },
        {
          heading: "Étape 2 — Afficher le code-barres quand disponible",
          body: [
            "Plusieurs marchands scannent le code-barres du reçu pour retrouver la transaction. Quand ce code-barres est détecté, ReFound peut l'afficher à l'écran. La disponibilité dépend du reçu et du marchand.",
          ],
        },
        {
          heading: "Étape 3 — Présenter l'image du reçu",
          body: [
            "Montrez l'image lisible du reçu, même si le papier original a commencé à s'effacer. Les informations utiles (marchand, date, articles, mode de paiement) sont regroupées au même endroit.",
          ],
          bullets: [
            "Code-barres affiché quand détecté",
            "Image du reçu prête à présenter",
            "Informations utiles regroupées",
          ],
        },
        {
          heading: "Et si le reçu est sur le téléphone de quelqu'un d'autre?",
          body: [
            "Votre conjoint est au magasin, mais la facture est sur votre téléphone? ReFound permet de [partager le reçu](/application-recus-famille) rapidement pour qu'il puisse présenter les informations utiles.",
          ],
        },
      ],
      faqTitle: "Questions fréquentes",
      faq: [
        { q: "Peut-on faire un retour avec un reçu numérique?", a: "Souvent oui, mais cela dépend de la politique du marchand. Certains acceptent une preuve d'achat numérique, d'autres préfèrent le reçu original." },
        { q: "Le magasin peut-il scanner le code-barres depuis mon téléphone?", a: "Quand le code-barres est détecté et affiché par ReFound, plusieurs marchands peuvent le scanner. Cela dépend du reçu et du marchand." },
        { q: "Un retour est-il garanti avec ReFound?", a: "Non. ReFound facilite la présentation des informations utiles, mais l'acceptation dépend de la politique du marchand et du produit." },
        { q: "Puis-je présenter un reçu partagé par mon conjoint?", a: "Oui. Le partage familial permet de présenter les informations utiles d'un reçu reçu d'un proche, selon la politique du marchand." },
      ],
      ctaTitle: "Présentez le bon reçu, simplement",
      ctaBody: "ReFound aide à retrouver et présenter le bon reçu, avec le code-barres quand disponible, pour faciliter vos retours.",
      relatedTitle: "Pour aller plus loin",
      related: [
        { label: "Application de retours en magasin", href: FR.returns, desc: "Faciliter vos retours avec votre téléphone." },
        { label: "Partage familial des reçus", href: FR.family, desc: "Partager un reçu avec un conjoint." },
        { label: "Application de reçus", href: FR.receipts, desc: "Scanner et retrouver vos reçus." },
      ],
    },
    en: {
      metaTitle: "How to Return Items in Store With Your Phone | ReFound",
      metaDescription:
        "Finding the right receipt, showing the barcode when available and presenting the receipt image can make in-store returns easier, based on merchant policy.",
      keywords:
        "in-store return with phone, return items with your phone, digital receipt return, receipt barcode, proof of purchase, ReFound",
      category: "Guide",
      date: "June 12, 2026",
      readingTime: "6 min read",
      breadcrumb: "Return in store with your phone",
      eyebrow: "Guide",
      h1: "How to return items in store with your phone",
      intro: [
        "Short answer: find the right receipt on your phone, show the barcode at the counter if one is detected, then present the receipt image. This can make the return easier, but acceptance always depends on the merchant's policy.",
      ],
      sections: [
        {
          heading: "Step 1 — Find the right receipt",
          body: [
            "Before heading to the store, find the product's receipt. With a [returns app](/en/returns-app), fast search by merchant, product or date saves you from digging.",
          ],
        },
        {
          heading: "Step 2 — Show the barcode when available",
          body: [
            "Many merchants scan the receipt barcode to find the transaction. When that barcode is detected, ReFound can display it on screen. Availability depends on the receipt and the merchant.",
          ],
        },
        {
          heading: "Step 3 — Present the receipt image",
          body: [
            "Show the readable receipt image, even if the original paper has started to fade. The useful information (merchant, date, items, payment method) is grouped in one place.",
          ],
          bullets: [
            "Barcode shown when detected",
            "Receipt image ready to present",
            "Useful information grouped together",
          ],
        },
        {
          heading: "What if the receipt is on someone else's phone?",
          body: [
            "Your partner is at the store, but the receipt is on your phone? ReFound lets you [share the receipt](/en/family-receipt-app) quickly so they can present the useful information.",
          ],
        },
      ],
      faqTitle: "Frequently asked questions",
      faq: [
        { q: "Can you return an item with a digital receipt?", a: "Often yes, but it depends on the merchant's policy. Some accept digital proof of purchase, others prefer the original receipt." },
        { q: "Can the store scan the barcode from my phone?", a: "When the barcode is detected and displayed by ReFound, many merchants can scan it. It depends on the receipt and the merchant." },
        { q: "Is a return guaranteed with ReFound?", a: "No. ReFound makes it easier to present the useful information, but acceptance depends on the merchant's policy and the product." },
        { q: "Can I present a receipt shared by my partner?", a: "Yes. Family sharing lets you present the useful information of a receipt received from a loved one, based on merchant policy." },
      ],
      ctaTitle: "Present the right receipt, simply",
      ctaBody: "ReFound helps you find and present the right receipt, with the barcode when available, to make returns easier.",
      relatedTitle: "Go further",
      related: [
        { label: "In-store returns app", href: EN.returns, desc: "Make returns easier with your phone." },
        { label: "Family receipt sharing", href: EN.family, desc: "Share a receipt with a partner." },
        { label: "Receipt app", href: EN.receipts, desc: "Scan and find your receipts." },
      ],
    },
  },

  /* ------------------------ best receipt app canada ------------------------ */
  {
    slug: "meilleure-application-recus-canada",
    frPath: "/blog/meilleure-application-recus-canada",
    enPath: "/en/blog/best-receipt-app-canada",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&q=80",
    imageAltFr: "Choisir la meilleure application de reçus au Canada",
    imageAltEn: "Choosing the best receipt app in Canada",
    datePublished: "2026-06-13T08:00:00-04:00",
    dateModified: "2026-06-13T08:00:00-04:00",
    fr: {
      metaTitle: "Quelle est la meilleure application de reçus au Canada? | ReFound",
      metaDescription:
        "Comment choisir la meilleure application de reçus au Canada selon vos besoins : retours, garanties, recherche ou famille. Guide clair pour les consommateurs.",
      keywords:
        "meilleure application de reçus, application de reçus Canada, application reçus, comparatif application reçus, reçus intelligents, ReFound",
      category: "Comparatif",
      date: "13 juin 2026",
      readingTime: "7 min de lecture",
      breadcrumb: "Meilleure application de reçus au Canada",
      eyebrow: "Comparatif",
      h1: "Quelle est la meilleure application de reçus au Canada?",
      intro: [
        "Réponse courte : la meilleure application de reçus dépend de votre besoin. Pour la comptabilité d'entreprise, une app de dépenses convient. Mais pour les consommateurs et les familles qui veulent gérer les retours, les garanties et les preuves d'achat du quotidien, une application pensée pour ces usages, comme ReFound, est plus adaptée.",
      ],
      sections: [
        {
          heading: "Choisir selon votre besoin",
          sub: [
            { heading: "Retours en magasin", body: ["Cherchez une app qui aide à retrouver le bon reçu et à afficher le code-barres quand disponible. Voir l'[application de retours](/application-retours-magasin)."] },
            { heading: "Garanties", body: ["Cherchez une app qui conserve la preuve d'achat et associe la garantie quand l'information est disponible. Voir l'[application de garanties](/application-garanties)."] },
            { heading: "Recherche et organisation", body: ["Cherchez une recherche rapide par marchand, produit et date pour retrouver un reçu en quelques secondes."] },
            { heading: "Usage familial", body: ["Cherchez le partage avec un conjoint ou un membre de la famille. Voir l'[application de reçus pour la famille](/application-recus-famille)."] },
            { heading: "Dépenses et taxes", body: ["Pour la comptabilité ou les dépenses d'entreprise, une app de dépenses dédiée reste plus appropriée. Ce n'est pas l'objectif de ReFound."] },
          ],
        },
        {
          heading: "Ce qui distingue ReFound",
          bullets: [
            "Reçus enrichis : nom et image du produit, catégorie, quand disponible",
            "Preuves d'achat intelligentes plutôt qu'une simple photo",
            "Aide aux retours et code-barres du reçu quand disponible",
            "Suivi des garanties quand l'information est disponible",
            "Partage de reçus avec un conjoint ou la famille",
            "Pensé pour les achats du quotidien au Canada",
          ],
        },
        {
          heading: "Pensé pour les consommateurs, pas pour la comptabilité",
          body: [
            "Beaucoup d'applications de reçus visent les reçus de dépenses d'affaires. ReFound se concentre sur les consommateurs et les familles : retours, garanties et preuves d'achat du quotidien. C'est une [application de reçus](/application-recus) pour la vraie vie de magasinage au Canada.",
          ],
        },
      ],
      faqTitle: "Questions fréquentes",
      faq: [
        { q: "Quelle application de reçus choisir au Canada?", a: "Cela dépend de votre besoin. Pour les retours, garanties et achats familiaux du quotidien, ReFound est pensé pour les consommateurs et les familles." },
        { q: "ReFound fonctionne-t-il avec les marchands canadiens?", a: "Oui. ReFound fonctionne avec les reçus de nombreux marchands canadiens comme Canadian Tire, RONA, Home Depot, Walmart, Costco, Best Buy, Décathlon, BMR, Canac, Dollarama, Tigre Géant et plus. L'enrichissement dépend du marchand et des données disponibles." },
        { q: "ReFound sert-il pour les dépenses d'entreprise?", a: "ReFound est pensé pour les consommateurs et les familles plutôt que pour la comptabilité ou les dépenses d'entreprise." },
        { q: "ReFound est-il gratuit?", a: "ReFound est gratuit à télécharger sur iPhone (App Store) et sur Android (Google Play)." },
      ],
      ctaTitle: "Une application de reçus pensée pour vous",
      ctaBody: "ReFound transforme vos reçus en preuves d'achat intelligentes pour les retours, les garanties et le partage familial.",
      relatedTitle: "Pour aller plus loin",
      related: [
        { label: "Application de reçus", href: FR.receipts, desc: "Scanner, enrichir et retrouver vos reçus." },
        { label: "Application de retours en magasin", href: FR.returns, desc: "Faciliter vos retours." },
        { label: "Application de garanties", href: FR.warranties, desc: "Conserver vos preuves d'achat." },
        { label: "Application de reçus pour la famille", href: FR.family, desc: "Partager les reçus du foyer." },
      ],
    },
    en: {
      metaTitle: "What Is the Best Receipt App in Canada? | ReFound",
      metaDescription:
        "How to choose the best receipt app in Canada for your needs: returns, warranties, search, family or expenses. A clear guide for consumers.",
      keywords:
        "best receipt app, receipt app Canada, receipt app, receipt app comparison, smart receipts, ReFound",
      category: "Comparison",
      date: "June 13, 2026",
      readingTime: "7 min read",
      breadcrumb: "Best receipt app in Canada",
      eyebrow: "Comparison",
      h1: "What is the best receipt app in Canada?",
      intro: [
        "Short answer: the best receipt app depends on your need. For business bookkeeping, an expense app fits. But for consumers and families who want to handle returns, warranties and everyday proof of purchase, an app built for those uses, like ReFound, is a better fit.",
      ],
      sections: [
        {
          heading: "Choose based on your need",
          sub: [
            { heading: "In-store returns", body: ["Look for an app that helps find the right receipt and show the barcode when available. See the [returns app](/en/returns-app)."] },
            { heading: "Warranties", body: ["Look for an app that keeps proof of purchase and attaches the warranty when the information is available. See the [warranty tracker app](/en/warranty-tracker-app)."] },
            { heading: "Search and organization", body: ["Look for fast search by merchant, product and date to find a receipt in seconds."] },
            { heading: "Family use", body: ["Look for sharing with a partner or family member. See the [family receipt app](/en/family-receipt-app)."] },
            { heading: "Expenses and taxes", body: ["For bookkeeping or business expenses, a dedicated expense app is more appropriate. That is not ReFound's goal."] },
          ],
        },
        {
          heading: "What sets ReFound apart",
          bullets: [
            "Enriched receipts: product name and image, category, when available",
            "Smart proof of purchase instead of just a photo",
            "Return assistance and receipt barcode when available",
            "Warranty tracking when the information is available",
            "Receipt sharing with a partner or family",
            "Built for everyday shopping in Canada",
          ],
        },
        {
          heading: "Built for consumers, not bookkeeping",
          body: [
            "Many receipt apps target business expense receipts. ReFound focuses on consumers and families: returns, warranties and everyday proof of purchase. It is a [receipt app](/en/receipt-app) for real shopping life in Canada.",
          ],
        },
      ],
      faqTitle: "Frequently asked questions",
      faq: [
        { q: "Which receipt app should you choose in Canada?", a: "It depends on your need. For everyday returns, warranties and family purchases, ReFound is built for consumers and families." },
        { q: "Does ReFound work with Canadian merchants?", a: "Yes. ReFound works with receipts from many Canadian merchants like Canadian Tire, RONA, Home Depot, Walmart, Costco, Best Buy, Decathlon, BMR, Canac, Dollarama, Giant Tiger and more. Enrichment depends on the merchant and available data." },
        { q: "Is ReFound for business expenses?", a: "ReFound is built for consumers and families rather than bookkeeping or business expenses." },
        { q: "Is ReFound free?", a: "ReFound is free to download on iPhone (App Store) and on Android (Google Play)." },
      ],
      ctaTitle: "A receipt app built for you",
      ctaBody: "ReFound turns receipts into smart proof of purchase for returns, warranties and family sharing.",
      relatedTitle: "Go further",
      related: [
        { label: "Receipt app", href: EN.receipts, desc: "Scan, enrich and find your receipts." },
        { label: "In-store returns app", href: EN.returns, desc: "Make returns easier." },
        { label: "Warranty tracker app", href: EN.warranties, desc: "Keep your proof of purchase." },
        { label: "Family receipt app", href: EN.family, desc: "Share household receipts." },
      ],
    },
  },

  /* ------------------------ share receipt family ------------------------ */
  {
    slug: "partager-recu-conjoint-famille",
    frPath: "/blog/partager-recu-conjoint-famille",
    enPath: "/en/blog/share-receipt-with-partner-family",
    image: "https://images.unsplash.com/photo-1556745757-8d76bdb6984b?w=1200&q=80",
    imageAltFr: "Partager un reçu avec son conjoint",
    imageAltEn: "Sharing a receipt with your partner",
    datePublished: "2026-06-14T08:00:00-04:00",
    dateModified: "2026-06-14T08:00:00-04:00",
    fr: {
      metaTitle: "Comment partager un reçu avec son conjoint ou sa famille | ReFound",
      metaDescription:
        "Utile quand une personne est au magasin et que le reçu est dans le téléphone de l'autre. Voici comment partager un reçu avec un proche grâce à ReFound.",
      keywords:
        "partager un reçu, partage de reçu conjoint, reçus de famille, partager reçu famille, preuve d'achat partagée, ReFound",
      category: "Guide",
      date: "14 juin 2026",
      readingTime: "5 min de lecture",
      breadcrumb: "Partager un reçu avec son conjoint",
      eyebrow: "Guide",
      h1: "Comment partager un reçu avec son conjoint ou sa famille",
      intro: [
        "Réponse courte : avec une application de reçus comme ReFound, vous pouvez partager un reçu avec un conjoint ou un membre de la famille. C'est utile quand une personne est au magasin pour un retour, mais que la facture est dans le compte ou le téléphone de l'autre.",
      ],
      sections: [
        {
          heading: "Le cas d'usage classique",
          body: [
            "Votre conjoint est au magasin, mais la facture est sur votre téléphone? ReFound vous aide à lui partager le reçu rapidement, pour qu'il puisse présenter les informations utiles au magasin, selon la politique du marchand.",
            "Les reçus importants ne restent plus coincés dans le téléphone d'une seule personne.",
          ],
        },
        {
          heading: "Pourquoi c'est pratique",
          bullets: [
            "Pratique pour les retours, les garanties et les achats familiaux",
            "Le bon reçu n'est plus bloqué dans un seul téléphone",
            "Les informations utiles sont prêtes à présenter au comptoir",
          ],
        },
        {
          heading: "Un partage maîtrisé",
          body: [
            "Le partage est pensé pour un conjoint ou un membre de la famille, pas pour une diffusion publique. Vous choisissez le reçu que vous partagez. Découvrez l'[application de reçus pour la famille](/application-recus-famille).",
          ],
        },
        {
          heading: "Et au comptoir des retours?",
          body: [
            "Une fois le reçu partagé, la personne au magasin peut présenter l'image du reçu et le code-barres quand disponible. Voir [comment faire un retour en magasin avec son téléphone](/blog/comment-faire-retour-magasin-avec-telephone).",
          ],
        },
      ],
      faqTitle: "Questions fréquentes",
      faq: [
        { q: "Comment partager un reçu avec mon conjoint?", a: "Avec ReFound, vous pouvez partager un reçu avec un conjoint ou un membre de la famille pour qu'il présente les informations utiles au magasin, selon la politique du marchand." },
        { q: "Est-ce que je peux partager un reçu avec n'importe qui?", a: "Le partage est pensé pour un conjoint ou un membre de la famille, pas pour une diffusion publique. Vous choisissez le reçu partagé." },
        { q: "Le reçu partagé est-il accepté pour un retour?", a: "Il aide à présenter les informations utiles, mais l'acceptation dépend de la politique du marchand et du produit." },
        { q: "Faut-il que toute la famille ait l'application?", a: "ReFound est disponible sur iPhone et Android. Le partage aide à transmettre les informations utiles d'un reçu à un proche au bon moment." },
      ],
      ctaTitle: "Le bon reçu, partagé au bon moment",
      ctaBody: "Avec ReFound, partagez un reçu avec un conjoint pour les retours, les garanties et les achats du quotidien.",
      relatedTitle: "Pour aller plus loin",
      related: [
        { label: "Application de reçus pour la famille", href: FR.family, desc: "Organiser et partager les reçus du foyer." },
        { label: "Application de retours en magasin", href: FR.returns, desc: "Présenter le bon reçu au comptoir." },
        { label: "Application de reçus", href: FR.receipts, desc: "Scanner et retrouver vos reçus." },
      ],
    },
    en: {
      metaTitle: "How to Share a Receipt With Your Partner or Family | ReFound",
      metaDescription:
        "Useful when one person is at the store but the receipt is on the other's phone. Here is how to share a receipt with your partner or family with ReFound.",
      keywords:
        "share a receipt, share receipt with partner, family receipts, share receipt family, shared proof of purchase, ReFound",
      category: "Guide",
      date: "June 14, 2026",
      readingTime: "5 min read",
      breadcrumb: "Share a receipt with your partner",
      eyebrow: "Guide",
      h1: "How to share a receipt with your partner or family",
      intro: [
        "Short answer: with a receipt app like ReFound, you can share a receipt with a partner or family member. It is useful when one person is at the store for a return, but the receipt is in the other's account or phone.",
      ],
      sections: [
        {
          heading: "The classic use case",
          body: [
            "Your partner is at the store, but the receipt is on your phone? ReFound helps you share it quickly, so they can present the useful information at the store, based on merchant policy.",
            "Important receipts no longer stay locked on one person's phone.",
          ],
        },
        {
          heading: "Why it is handy",
          bullets: [
            "Useful for returns, warranties and family purchases",
            "The right receipt is no longer stuck on one phone",
            "The useful information is ready to present at the counter",
          ],
        },
        {
          heading: "Sharing you control",
          body: [
            "Sharing is meant for a partner or family member, not public broadcasting. You choose which receipt you share. Discover the [family receipt app](/en/family-receipt-app).",
          ],
        },
        {
          heading: "And at the return counter?",
          body: [
            "Once the receipt is shared, the person at the store can present the receipt image and the barcode when available. See [how to return items in store with your phone](/en/blog/how-to-return-items-in-store-with-your-phone).",
          ],
        },
      ],
      faqTitle: "Frequently asked questions",
      faq: [
        { q: "How do I share a receipt with my partner?", a: "With ReFound, you can share a receipt with a partner or family member so they can present the useful information at the store, based on merchant policy." },
        { q: "Can I share a receipt with anyone?", a: "Sharing is meant for a partner or family member, not public broadcasting. You choose which receipt you share." },
        { q: "Is a shared receipt accepted for a return?", a: "It helps present the useful information, but acceptance depends on the merchant's policy and the product." },
        { q: "Does the whole family need the app?", a: "ReFound is available on iPhone and Android. Sharing helps pass the useful information of a receipt to a loved one at the right moment." },
      ],
      ctaTitle: "The right receipt, shared at the right moment",
      ctaBody: "With ReFound, share a receipt with a partner for returns, warranties and everyday purchases.",
      relatedTitle: "Go further",
      related: [
        { label: "Family receipt app", href: EN.family, desc: "Organize and share household receipts." },
        { label: "In-store returns app", href: EN.returns, desc: "Present the right receipt at the counter." },
        { label: "Receipt app", href: EN.receipts, desc: "Scan and find your receipts." },
      ],
    },
  },

  /* ------------- store warranty vs manufacturer warranty ------------- */
  {
    slug: "garantie-magasin-vs-garantie-manufacturier",
    frPath: "/blog/garantie-magasin-vs-garantie-manufacturier",
    enPath: "/en/blog/store-warranty-vs-manufacturer-warranty",
    image: "https://images.unsplash.com/photo-1556909212-d5b604d0c90d?w=1200&q=80",
    imageAltFr: "Électroménagers couverts par une garantie",
    imageAltEn: "Home appliances covered by a warranty",
    datePublished: "2026-06-15T08:00:00-04:00",
    dateModified: "2026-06-15T08:00:00-04:00",
    fr: {
      metaTitle: "Garantie de satisfaction du magasin vs garantie du manufacturier | ReFound",
      metaDescription:
        "Garantie de satisfaction du magasin (2 semaines à 3 mois) ou garantie du fabricant : voici la différence et comment ReFound aide à suivre les deux.",
      keywords:
        "garantie de satisfaction magasin, garantie manufacturier, échange remboursement, délai de retour facture, garantie produit, Mastercraft Canadian Tire, suivi de garantie, preuve d'achat, ReFound",
      category: "Guide",
      date: "15 juin 2026",
      readingTime: "7 min de lecture",
      breadcrumb: "Garantie de satisfaction vs garantie du manufacturier",
      eyebrow: "Guide",
      h1: "Garantie de satisfaction du magasin et garantie du manufacturier : quelle est la différence?",
      intro: [
        "Réponse courte : la garantie de satisfaction du magasin est le délai pour échanger ou se faire rembourser, indiqué directement sur la facture — souvent de 2 semaines à 3 mois. La garantie du manufacturier, elle, est offerte par le fabricant pour un produit précis et sa durée varie d'un article à l'autre. Les deux reposent sur une même chose : retrouver le bon reçu.",
      ],
      sections: [
        {
          heading: "La garantie de satisfaction du magasin",
          body: [
            "C'est la politique du détaillant qui vous permet de retourner un article pour un échange ou un remboursement, simplement parce que vous changez d'idée ou n'êtes pas satisfait. Le délai est habituellement imprimé directement sur la facture et varie souvent de 2 semaines à 3 mois selon le marchand.",
            "Elle est gratuite et fixée par le magasin. Passé le délai indiqué, le marchand n'est plus obligé de reprendre l'article. Pour s'en prévaloir, il faut généralement présenter le reçu et respecter la fenêtre indiquée (article non utilisé, emballage d'origine, etc., selon la politique).",
          ],
        },
        {
          heading: "La garantie du manufacturier",
          body: [
            "C'est une garantie offerte par le fabricant pour un produit en particulier. Sa durée et sa couverture varient énormément d'un article à l'autre : un outil, un électroménager et un appareil électronique vendus sous la même enseigne peuvent avoir des garanties complètement différentes.",
            "Par exemple, les produits Mastercraft vendus chez Canadian Tire ont chacun leur propre garantie : contrairement à la croyance d'une « garantie à vie », elle change selon le produit. Il faut donc vérifier la garantie propre à l'article — et garder la preuve d'achat, presque toujours exigée pour une réclamation. Encore faut-il savoir quel article se cache derrière la ligne imprimée : voir [comment ReFound lit un reçu Canadian Tire](/marchands/recus-canadian-tire).",
          ],
        },
        {
          heading: "Laquelle s'applique selon la situation?",
          body: [
            "Tout dépend de la raison du retour. La satisfaction du magasin sert quand vous changez d'idée; la garantie du manufacturier sert quand le produit est défectueux.",
          ],
          bullets: [
            "Vous changez d'idée ou le produit ne convient pas → garantie de satisfaction du magasin (dans le délai de la facture)",
            "Le produit est défectueux ou brise après le délai de retour → garantie du manufacturier propre à l'article",
            "Dans les deux cas → la preuve d'achat (le reçu) est exigée",
          ],
        },
        {
          heading: "Comment ReFound aide à suivre les deux",
          body: [
            "Le vrai défi, c'est de retrouver le bon reçu avant la fin du délai d'échange, ou des mois plus tard pour une garantie. [ReFound](/application-garanties) conserve une copie lisible de chaque preuve d'achat, organisée par marchand, produit et date. Un reçu contient souvent plusieurs articles : ReFound aide à trouver la garantie du manufacturier propre à chacun, quand l'information est disponible.",
          ],
          bullets: [
            "Le délai d'échange ou de remboursement estimé à partir de la facture, quand disponible",
            "La garantie du manufacturier trouvée pour chaque article du reçu, quand l'information est disponible",
            "Recherche rapide par marchand, produit ou date au moment d'un retour ou d'une réclamation",
          ],
        },
      ],
      comparison: {
        title: "Garantie de satisfaction du magasin vs garantie du manufacturier",
        intro:
          "Un aperçu des principales différences. Les conditions exactes varient selon le marchand et le produit.",
        refoundLabel: "Satisfaction du magasin",
        othersLabel: "Garantie du manufacturier",
        rows: [
          { label: "Qui l'offre", refound: "Le détaillant / magasin", others: "Le fabricant du produit" },
          { label: "À quoi ça sert", refound: "Échange ou remboursement", others: "Réparer/remplacer un défaut" },
          { label: "Durée typique", refound: "2 semaines à 3 mois", others: "Variable selon le produit" },
          { label: "Où c'est indiqué", refound: "Sur la facture", others: "Documentation du fabricant" },
          { label: "Raison du retour", refound: "Changement d'idée, insatisfaction", others: "Défaut ou bris couvert" },
          { label: "Preuve demandée", refound: "Le reçu", others: "Le reçu (preuve d'achat)" },
          { label: "Où réclamer", refound: "Au magasin", others: "Auprès du fabricant (ou du magasin)" },
        ],
      },
      faqTitle: "Questions fréquentes",
      faq: [
        { q: "Où est indiquée la garantie de satisfaction du magasin?", a: "Habituellement directement sur la facture ou le reçu : on y trouve le délai pour échanger ou se faire rembourser, souvent de 2 semaines à 3 mois selon le marchand." },
        { q: "La garantie du manufacturier est-elle toujours « à vie »?", a: "Non. C'est une croyance répandue, mais la durée varie d'un produit à l'autre. Par exemple, les produits Mastercraft de Canadian Tire ont chacun leur propre garantie : il faut vérifier celle de l'article précis." },
        { q: "Ai-je besoin du reçu dans les deux cas?", a: "Oui, presque toujours. Le reçu prouve la date et le lieu de l'achat — sans lui, un échange dans le délai ou une réclamation de garantie devient beaucoup plus difficile." },
        { q: "ReFound suit-il les deux?", a: "ReFound conserve la preuve d'achat de chaque produit, peut estimer le délai de retour à partir de la facture et trouver la garantie du manufacturier pour chaque article du reçu, quand l'information est disponible." },
      ],
      ctaTitle: "Gardez vos délais et garanties au même endroit",
      ctaBody: "ReFound conserve vos reçus, estime le délai d'échange et associe la garantie du manufacturier quand l'information est disponible — prêts au bon moment.",
      relatedTitle: "Pour aller plus loin",
      related: [
        { label: "Application de garanties", href: FR.warranties, desc: "Suivre vos garanties et preuves d'achat." },
        { label: "Conserver ses reçus pour les garanties", href: "/blog/comment-conserver-recus-garanties", desc: "Garder la bonne preuve d'achat." },
        { label: "Application de reçus", href: FR.receipts, desc: "Scanner et enrichir vos reçus." },
      ],
    },
    en: {
      metaTitle: "Store Satisfaction Guarantee vs Manufacturer Warranty | ReFound",
      metaDescription:
        "Store satisfaction guarantee (2 weeks to 3 months) versus manufacturer warranty: here's the difference and how ReFound helps you track both.",
      keywords:
        "store satisfaction guarantee, manufacturer warranty, exchange refund window, return deadline receipt, product warranty, Mastercraft Canadian Tire, warranty tracker, proof of purchase, ReFound",
      category: "Guide",
      date: "June 15, 2026",
      readingTime: "7 min read",
      breadcrumb: "Store satisfaction vs manufacturer warranty",
      eyebrow: "Guide",
      h1: "Store satisfaction guarantee vs manufacturer warranty: what's the difference?",
      intro: [
        "Short answer: the store's satisfaction guarantee is the window to exchange or get a refund, printed right on the receipt — usually 2 weeks to 3 months. The manufacturer warranty is offered by the maker for a specific product, and its length varies from one item to another. Both come down to one thing: finding the right receipt.",
      ],
      sections: [
        {
          heading: "The store satisfaction guarantee",
          body: [
            "This is the retailer's policy that lets you return an item for an exchange or refund, simply because you changed your mind or aren't satisfied. The window is usually printed right on the receipt and often ranges from 2 weeks to 3 months depending on the merchant.",
            "It's free and set by the store. Once the printed window passes, the merchant is no longer required to take the item back. To use it, you generally present the receipt and stay within the window (unused item, original packaging, etc., per the policy).",
          ],
        },
        {
          heading: "The manufacturer warranty",
          body: [
            "This is a warranty offered by the maker for a specific product. Its length and coverage vary widely from one item to another: a tool, an appliance and an electronic device sold under the same store banner can have completely different warranties.",
            "For example, Mastercraft products sold at Canadian Tire each have their own warranty: despite the belief in a \"lifetime warranty,\" it changes by product. So you need to check the warranty specific to the item — and keep the proof of purchase, almost always required for a claim. That assumes you can tell which item the printed line refers to: see [how ReFound reads a Canadian Tire receipt](/en/merchants/canadian-tire-receipts).",
          ],
        },
        {
          heading: "Which one applies in your situation?",
          body: [
            "It depends on the reason for the return. The store's satisfaction guarantee is for changing your mind; the manufacturer warranty is for a defective product.",
          ],
          bullets: [
            "You changed your mind or the product doesn't fit → store satisfaction guarantee (within the receipt's window)",
            "The product is defective or breaks after the return window → the manufacturer warranty specific to the item",
            "In both cases → proof of purchase (the receipt) is required",
          ],
        },
        {
          heading: "How ReFound helps you track both",
          body: [
            "The real challenge is finding the right receipt before the exchange window closes, or months later for a warranty. [ReFound](/en/warranty-tracker-app) keeps a readable copy of every proof of purchase, organized by merchant, product and date. A receipt often has several items: ReFound helps find the manufacturer warranty for each one, when the information is available.",
          ],
          bullets: [
            "The estimated exchange or refund deadline from the receipt, when available",
            "The manufacturer warranty found for each item on the receipt, when the information is available",
            "Fast search by merchant, product or date when it's time for a return or a claim",
          ],
        },
      ],
      comparison: {
        title: "Store satisfaction guarantee vs manufacturer warranty",
        intro:
          "An overview of the main differences. Exact terms vary by merchant and product.",
        refoundLabel: "Store satisfaction",
        othersLabel: "Manufacturer warranty",
        rows: [
          { label: "Who offers it", refound: "The retailer / store", others: "The product's maker" },
          { label: "What it's for", refound: "Exchange or refund", others: "Repair/replace a defect" },
          { label: "Typical length", refound: "2 weeks to 3 months", others: "Varies by product" },
          { label: "Where it's stated", refound: "On the receipt", others: "Manufacturer documentation" },
          { label: "Reason for return", refound: "Changed mind, not satisfied", others: "Covered defect or breakage" },
          { label: "Proof required", refound: "The receipt", others: "The receipt (proof of purchase)" },
          { label: "Where to claim", refound: "At the store", others: "With the manufacturer (or store)" },
        ],
      },
      faqTitle: "Frequently asked questions",
      faq: [
        { q: "Where is the store's satisfaction guarantee shown?", a: "Usually right on the receipt: it shows the window to exchange or get a refund, often 2 weeks to 3 months depending on the merchant." },
        { q: "Is the manufacturer warranty always \"lifetime\"?", a: "No. It's a common belief, but the length varies from one product to another. For example, Canadian Tire's Mastercraft products each have their own warranty: you need to check the one for the specific item." },
        { q: "Do I need the receipt in both cases?", a: "Yes, almost always. The receipt proves the date and place of purchase — without it, an exchange within the window or a warranty claim becomes much harder." },
        { q: "Does ReFound track both?", a: "ReFound keeps the proof of purchase for each product, can estimate the return window from the receipt and find the manufacturer warranty for each item on the receipt, when the information is available." },
      ],
      ctaTitle: "Keep your deadlines and warranties in one place",
      ctaBody: "ReFound keeps your receipts, estimates the exchange window and attaches the manufacturer warranty when the information is available — ready when you need them.",
      relatedTitle: "Go further",
      related: [
        { label: "Warranty tracker app", href: EN.warranties, desc: "Track your warranties and proofs of purchase." },
        { label: "How to keep receipts for warranties", href: "/en/blog/how-to-keep-receipts-for-warranties", desc: "Keep the right proof of purchase." },
        { label: "Receipt app", href: EN.receipts, desc: "Scan and enrich your receipts." },
      ],
    },
  },

  /* -------------------- how to choose a receipt app -------------------- */
  {
    slug: "comment-choisir-application-recus",
    frPath: "/blog/comment-choisir-application-recus",
    enPath: "/en/blog/how-to-choose-receipt-app",
    image: "https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=1200&q=80",
    imageAltFr: "Comparaison d'applications de reçus sur un téléphone",
    imageAltEn: "Comparing receipt apps on a phone",
    datePublished: "2026-07-10T08:00:00-04:00",
    dateModified: "2026-07-10T08:00:00-04:00",
    fr: {
      metaTitle: "Comment choisir une application de reçus (2026) | ReFound",
      metaDescription:
        "Précision OCR, stockage cloud sécurisé, catégories et filtres : les 3 critères essentiels pour choisir une application de reçus en 2026, avec comparatif.",
      keywords:
        "comment choisir application de reçus, meilleure application de reçus 2026, précision OCR reçus, stockage cloud reçus, catégoriser reçus, comparatif application reçus, ReFound vs Expensify, ReFound",
      category: "Guide d'achat",
      date: "10 juillet 2026",
      readingTime: "9 min de lecture",
      breadcrumb: "Comment choisir une application de reçus",
      eyebrow: "Guide d'achat",
      h1: "Comment choisir une application de reçus : 3 critères essentiels (2026)",
      intro: [
        "Réponse courte : jugez une application de reçus sur trois critères — la précision de l'OCR (des données fiables), le stockage cloud sécurisé (des reçus qui survivent à votre téléphone) et la catégorisation avec filtres (des reçus qu'on retrouve). Tout le reste est secondaire.",
        "Une application de reçus a un seul travail : que votre preuve d'achat soit exacte, en sécurité et retrouvable au moment où vous en avez besoin. Voici comment évaluer chaque critère, avec un comparatif des applications populaires au Canada.",
      ],
      sections: [
        {
          heading: "Pourquoi ces trois critères font toute la différence",
          body: [
            "Chacun des trois critères correspond à une façon de perdre une preuve d'achat. Un OCR imprécis, et les données de votre reçu sont fausses sans que vous le sachiez. Pas de sauvegarde cloud, et un téléphone perdu emporte tous vos reçus. Pas de catégories ni de filtres, et le bon reçu existe quelque part… mais introuvable au comptoir des retours.",
            "Les fonctionnalités « bonus » (récompenses, rapports, exportations) ne compensent jamais une faiblesse sur l'un de ces trois fondamentaux.",
          ],
        },
        {
          heading: "Critère 1 — La précision de l'OCR : des données fiables ou rien",
          body: [
            "L'OCR (reconnaissance optique de caractères) est la technologie qui lit votre reçu et le transforme en données : marchand, date, total, taxes, articles. Si cette lecture se trompe, votre reçu numérique ment. Un total mal lu ou une date erronée, c'est une preuve d'achat qu'on ne peut plus utiliser — et l'erreur ne se découvre souvent qu'au moment du retour.",
            "Fiez-vous aux mesures indépendantes plutôt qu'aux promesses. Le benchmark [AIMultiple](https://aimultiple.com/ocr-accuracy) mesure 98 % de précision de texte pour Google Cloud Vision, sur un banc d'essai qui inclut des reçus et des factures. Le benchmark d'APIs OCR de [Nanonets](https://nanonets.com/blog/identifying-the-best-ocr-api/), mené sur SROIE, un jeu de données de reçus numérisés, classe aussi Google Cloud Vision meilleur choix global pour l'équilibre précision-vitesse.",
            "ReFound est propulsé par cette même technologie Google Vision, et fait appel au modèle Gemini le plus avancé de Google — que [Google positionne comme un leader en compréhension de documents](https://blog.google/innovation-and-ai/technology/developers-tools/gemini-3-pro-vision/) difficiles — pour les reçus effacés, froissés ou complexes. En plus de lire le texte, ReFound enrichit les articles : les codes SKU obscurs deviennent de vrais noms de produits avec images. Les détails : [la précision OCR de ReFound](/fonctionnalites/precision-ocr).",
          ],
        },
        {
          heading: "Critère 2 — Le stockage cloud sécurisé : vos reçus survivent à votre téléphone",
          body: [
            "Un reçu qui vit uniquement dans la galerie photo disparaît avec le téléphone : perte, vol, bris, écran noir. La preuve d'achat de votre laveuse ne devrait pas dépendre de la survie d'un appareil qu'on échappe régulièrement.",
            "Avec ReFound, vos reçus sont conservés sur votre appareil, avec la possibilité d'activer la sauvegarde cloud pour les retrouver en cas de besoin. Ils restent privés et protégés, et la base de données principale de ReFound est hébergée au Canada. Un nouveau téléphone, et tout est là après la connexion.",
            "À vérifier avant de choisir : où les données sont hébergées, si elles restent privées, et si vous gardez le contrôle de la sauvegarde. Les détails : [le stockage cloud sécurisé de ReFound](/fonctionnalites/stockage-cloud-securise).",
          ],
        },
        {
          heading: "Critère 3 — Catégorisation et filtres : retrouver un reçu en secondes",
          body: [
            "Le troisième critère est celui qu'on oublie jusqu'au jour où on en a besoin : retrouver le bon reçu, vite. Une pile de reçus numérisés sans organisation n'est qu'un tiroir numérique.",
            "ReFound classe chaque reçu automatiquement par catégorie et vous laisse créer vos propres catégories. Les filtres suivent la façon dont on se souvient vraiment d'un achat : par magasin, par carte de paiement ou par montant.",
            "La force distinctive de ReFound reste la recherche par vrai nom de produit : parce que les articles sont enrichis au-delà des codes SKU, chercher « perceuse » retrouve le reçu de la perceuse — même des mois plus tard. Les détails : [la catégorisation et les filtres de ReFound](/fonctionnalites/categorisation-filtres).",
          ],
        },
        {
          heading: "Notre verdict",
          body: [
            "Le tableau ci-dessous résume la comparaison. Les applications populaires ne sont pas mauvaises : elles sont conçues pour autre chose. Expensify et QuickBooks sont d'excellents outils de dépenses et de comptabilité d'entreprise. Receipt Hog et Checkout 51 sont des applications de récompenses qui collectent vos reçus, pas des organisateurs. Smart Receipts vise les rapports de dépenses avec une saisie surtout manuelle.",
            "Pour un consommateur ou une famille au Canada qui veut des reçus exacts, en sécurité et retrouvables — pour les retours, les garanties et le quotidien — ReFound est l'application conçue précisément pour ces trois critères, et elle est gratuite.",
          ],
        },
      ],
      matrix: {
        title: "Comparatif : les applications de reçus face aux 3 critères",
        intro:
          "Chaque application est évaluée sur son positionnement public en 2026, selon les trois critères essentiels.",
        columns: ["Critère", "ReFound", "Expensify", "Receipt Hog", "Checkout 51", "Smart Receipts", "QuickBooks"],
        rows: [
          {
            label: "Pensée pour",
            cells: [
              "Consommateurs et familles",
              "Notes de frais d'entreprise",
              "Récompenses contre vos reçus",
              "Cash back sur des marques",
              "Rapports de dépenses",
              "Comptabilité d'entreprise",
            ],
          },
          {
            label: "Précision OCR",
            cells: [
              "Google Vision + Gemini pour les reçus difficiles",
              "OCR orienté dépenses (totaux, taxes)",
              "Lecture orientée marques partenaires",
              "Lecture orientée offres",
              "Saisie surtout manuelle",
              "OCR orienté comptabilité",
            ],
          },
          {
            label: "Vrais noms de produits + images",
            cells: ["Oui", "Non", "Non", "Non", "Non", "Non"],
          },
          {
            label: "Stockage cloud sécurisé",
            cells: [
              "Oui — serveurs au Canada",
              "Oui — orienté entreprise",
              "Pas un coffre à reçus",
              "Pas un coffre à reçus",
              "Fichiers locaux / exports",
              "Oui — orienté comptabilité",
            ],
          },
          {
            label: "Catégories + filtres (magasin, carte, montant)",
            cells: [
              "Auto + personnalisées",
              "Catégories de dépenses pro",
              "Non",
              "Non",
              "Catégories manuelles",
              "Catégories comptables",
            ],
          },
          {
            label: "Aide aux retours et garanties",
            cells: ["Oui", "Non", "Non", "Non", "Non", "Non"],
          },
          {
            label: "Prix",
            cells: [
              "Gratuit",
              "Abonnement pour les fonctions avancées",
              "« Gratuit » — monétisé par vos données",
              "« Gratuit » — offres des marques",
              "Gratuit avec options payantes",
              "Abonnement mensuel",
            ],
          },
        ],
        note:
          "Comparaison basée sur le positionnement public de chaque application en 2026. Receipt Hog et Checkout 51 sont des applications de récompenses, pas des organisateurs de reçus; Expensify et QuickBooks visent les dépenses et la comptabilité d'entreprise.",
      },
      faqTitle: "Questions fréquentes",
      faq: [
        {
          q: "Quelle application de reçus a le meilleur OCR ?",
          a: "ReFound est propulsé par Google Vision, mesuré à 98 % de précision de texte par le benchmark indépendant AIMultiple et classé meilleur choix global par le benchmark de Nanonets sur des reçus numérisés (SROIE). Pour les reçus effacés ou difficiles à lire, ReFound utilise en plus le modèle Gemini le plus avancé de Google.",
        },
        {
          q: "Mes reçus sont-ils sauvegardés si je perds mon téléphone ?",
          a: "Avec ReFound, oui, lorsque la sauvegarde cloud est activée. Vos reçus restent privés et protégés, et sont conservés de façon sécurisée pour que vous puissiez les retrouver en vous connectant depuis un autre appareil.",
        },
        {
          q: "Quelle app permet de filtrer les reçus par magasin ou par carte ?",
          a: "ReFound filtre les reçus par magasin, par carte de paiement et par montant, en plus des catégories automatiques et personnalisées. La recherche fonctionne aussi par vrai nom de produit plutôt que par code SKU.",
        },
        {
          q: "Expensify ou QuickBooks ne suffisent-ils pas ?",
          a: "Ce sont d'excellents outils pour les notes de frais et la comptabilité d'entreprise, mais ils classent les achats en catégories fiscales et visent les rapports de dépenses. Pour les retours, les garanties et les achats personnels du quotidien, une application pensée pour les consommateurs comme ReFound est mieux adaptée.",
        },
        {
          q: "Receipt Hog et Checkout 51 sont-elles des applications de reçus ?",
          a: "Ce sont d'abord des applications de récompenses : vous scannez vos reçus en échange de points ou de cash back, et vos données d'achat alimentent des études de marché. Elles ne sont pas conçues pour organiser, retrouver ou réutiliser vos reçus.",
        },
        {
          q: "Combien coûte ReFound ?",
          a: "ReFound est gratuit à télécharger sur iPhone et Android, et applique une politique stricte de non-revente de vos données d'achat.",
        },
      ],
      ctaTitle: "L'application qui coche les 3 critères",
      ctaBody:
        "OCR propulsé par Google Vision et Gemini, sauvegarde sur des serveurs au Canada, catégories et filtres par magasin, carte ou montant : essayez ReFound gratuitement.",
      relatedTitle: "Pour aller plus loin",
      related: [
        { label: "Précision OCR", href: "/fonctionnalites/precision-ocr", desc: "Des reçus lus correctement, du premier coup." },
        { label: "Stockage cloud sécurisé", href: "/fonctionnalites/stockage-cloud-securise", desc: "Vos reçus survivent à votre téléphone." },
        { label: "Catégorisation et filtres", href: "/fonctionnalites/categorisation-filtres", desc: "Retrouvez n'importe quel reçu en secondes." },
        { label: "Application de reçus", href: FR.receipts, desc: "Scanner, enrichir et retrouver vos reçus." },
      ],
    },
    en: {
      metaTitle: "How to Choose a Receipt App: 3 Key Criteria (2026) | ReFound",
      metaDescription:
        "OCR accuracy, secure cloud storage, categories and filters: the 3 essential criteria for choosing a receipt app in 2026, with a comparison table.",
      keywords:
        "how to choose a receipt app, best receipt app 2026, receipt OCR accuracy, receipt cloud storage, categorize receipts, receipt app comparison, ReFound vs Expensify, ReFound",
      category: "Buying guide",
      date: "July 10, 2026",
      readingTime: "9 min read",
      breadcrumb: "How to choose a receipt app",
      eyebrow: "Buying guide",
      h1: "How to choose a receipt app: 3 key criteria (2026)",
      intro: [
        "Short answer: judge a receipt app on three criteria — OCR accuracy (data you can trust), secure cloud storage (receipts that outlive your phone) and categorization with filters (receipts you can actually find). Everything else is secondary.",
        "A receipt app has one job: making sure your proof of purchase is accurate, safe and findable the moment you need it. Here is how to evaluate each criterion, with a comparison of the apps popular in Canada.",
      ],
      sections: [
        {
          heading: "Why these three criteria make all the difference",
          body: [
            "Each criterion maps to a way of losing proof of purchase. Inaccurate OCR, and your receipt's data is wrong without you knowing. No cloud backup, and a lost phone takes every receipt with it. No categories or filters, and the right receipt exists somewhere… but not at the return counter.",
            "\"Bonus\" features (rewards, reports, exports) never make up for a weakness on one of these three fundamentals.",
          ],
        },
        {
          heading: "Criterion 1 — OCR accuracy: trustworthy data or nothing",
          body: [
            "OCR (optical character recognition) is the technology that reads your receipt and turns it into data: merchant, date, total, taxes, items. If that reading is wrong, your digital receipt lies. A misread total or date is proof of purchase you can no longer use — and you often only find out at return time.",
            "Trust independent measurements over promises. The [AIMultiple benchmark](https://aimultiple.com/ocr-accuracy) measures 98% text accuracy for Google Cloud Vision, on a test set that includes receipts and invoices. The [Nanonets OCR API benchmark](https://nanonets.com/blog/identifying-the-best-ocr-api/), run on SROIE, a scanned-receipt dataset, also ranks Google Cloud Vision as the best overall performer balancing accuracy and speed.",
            "ReFound is powered by that same Google Vision technology, and calls on Google's most advanced Gemini model — which [Google positions as a leader in document understanding](https://blog.google/innovation-and-ai/technology/developers-tools/gemini-3-pro-vision/) for messy documents — for faded, crumpled or complex receipts. Beyond reading the text, ReFound enriches items: cryptic SKU codes become real product names with images. Details: [ReFound's OCR accuracy](/en/features/ocr-accuracy).",
          ],
        },
        {
          heading: "Criterion 2 — Secure cloud storage: receipts that outlive your phone",
          body: [
            "A receipt that lives only in your camera roll disappears with the phone: loss, theft, damage, dead screen. The proof of purchase for your washing machine should not depend on the survival of a device we all drop regularly.",
            "With ReFound, your receipts are kept on your device, with the option to turn on cloud backup so you can find them when you need them. They stay private and protected, and ReFound's main database is hosted in Canada. Get a new phone, sign in, and everything is there.",
            "Before you choose, check: where the data is hosted, whether it stays private, and whether you stay in control of the backup. Details: [ReFound's secure cloud storage](/en/features/secure-cloud-storage).",
          ],
        },
        {
          heading: "Criterion 3 — Categories and filters: find a receipt in seconds",
          body: [
            "The third criterion is the one people forget until the day they need it: finding the right receipt, fast. A pile of scanned receipts without organization is just a digital drawer.",
            "ReFound sorts every receipt automatically by category and lets you create your own categories. Filters follow the way people actually remember purchases: by store, by payment card or by amount.",
            "ReFound's strongest differentiator remains search by real product name: because items are enriched beyond SKU codes, searching \"drill\" finds the drill's receipt — even months later. Details: [ReFound's categories and filters](/en/features/categories-filters).",
          ],
        },
        {
          heading: "Our verdict",
          body: [
            "The table below sums up the comparison. The popular apps are not bad: they are built for something else. Expensify and QuickBooks are excellent business expense and accounting tools. Receipt Hog and Checkout 51 are rewards apps that collect your receipts, not organizers. Smart Receipts targets expense reports with mostly manual entry.",
            "For a consumer or family in Canada who wants receipts that are accurate, safe and findable — for returns, warranties and everyday life — ReFound is the app built precisely for these three criteria, and it is free.",
          ],
        },
      ],
      matrix: {
        title: "Comparison: receipt apps against the 3 criteria",
        intro:
          "Each app is assessed on its public positioning in 2026, against the three key criteria.",
        columns: ["Criterion", "ReFound", "Expensify", "Receipt Hog", "Checkout 51", "Smart Receipts", "QuickBooks"],
        rows: [
          {
            label: "Built for",
            cells: [
              "Consumers and families",
              "Business expense reports",
              "Rewards for your receipts",
              "Cash back on brands",
              "Expense reports",
              "Business accounting",
            ],
          },
          {
            label: "OCR accuracy",
            cells: [
              "Google Vision + Gemini for hard receipts",
              "Expense-oriented OCR (totals, taxes)",
              "Reading geared to partner brands",
              "Reading geared to offers",
              "Mostly manual entry",
              "Accounting-oriented OCR",
            ],
          },
          {
            label: "Real product names + images",
            cells: ["Yes", "No", "No", "No", "No", "No"],
          },
          {
            label: "Secure cloud storage",
            cells: [
              "Yes — servers in Canada",
              "Yes — business-oriented",
              "Not a receipt vault",
              "Not a receipt vault",
              "Local files / exports",
              "Yes — accounting-oriented",
            ],
          },
          {
            label: "Categories + filters (store, card, amount)",
            cells: [
              "Auto + custom",
              "Business expense categories",
              "No",
              "No",
              "Manual categories",
              "Accounting categories",
            ],
          },
          {
            label: "Returns and warranty help",
            cells: ["Yes", "No", "No", "No", "No", "No"],
          },
          {
            label: "Price",
            cells: [
              "Free",
              "Subscription for advanced features",
              "“Free” — monetized via your data",
              "“Free” — brand offers",
              "Free with paid options",
              "Monthly subscription",
            ],
          },
        ],
        note:
          "Comparison based on each app's public positioning in 2026. Receipt Hog and Checkout 51 are rewards apps, not receipt organizers; Expensify and QuickBooks target business expenses and accounting.",
      },
      faqTitle: "Frequently asked questions",
      faq: [
        {
          q: "Which receipt app has the best OCR?",
          a: "ReFound is powered by Google Vision, measured at 98% text accuracy by the independent AIMultiple benchmark and ranked best overall performer by the Nanonets benchmark on scanned receipts (SROIE). For faded or hard-to-read receipts, ReFound also uses Google's most advanced Gemini model.",
        },
        {
          q: "Are my receipts backed up if I lose my phone?",
          a: "With ReFound, yes, when cloud backup is on. Your receipts stay private and protected, and they're kept securely so you can find them by signing in from another device.",
        },
        {
          q: "Which app lets you filter receipts by store or by card?",
          a: "ReFound filters receipts by store, by payment card and by amount, on top of automatic and custom categories. Search also works by real product name rather than SKU code.",
        },
        {
          q: "Aren't Expensify or QuickBooks enough?",
          a: "They are excellent tools for expense reports and business accounting, but they sort purchases into tax categories and target expense reporting. For returns, warranties and everyday personal purchases, a consumer-focused app like ReFound is a better fit.",
        },
        {
          q: "Are Receipt Hog and Checkout 51 receipt apps?",
          a: "They are first and foremost rewards apps: you scan receipts in exchange for points or cash back, and your purchase data feeds market research. They are not designed to organize, find or reuse your receipts.",
        },
        {
          q: "How much does ReFound cost?",
          a: "ReFound is free to download on iPhone and Android, and applies a strict no-selling policy for your purchase data.",
        },
      ],
      ctaTitle: "The app that checks all 3 criteria",
      ctaBody:
        "OCR powered by Google Vision and Gemini, backup on servers in Canada, categories and filters by store, card or amount: try ReFound for free.",
      relatedTitle: "Go further",
      related: [
        { label: "OCR accuracy", href: "/en/features/ocr-accuracy", desc: "Receipts read correctly, the first time." },
        { label: "Secure cloud storage", href: "/en/features/secure-cloud-storage", desc: "Your receipts outlive your phone." },
        { label: "Categories and filters", href: "/en/features/categories-filters", desc: "Find any receipt in seconds." },
        { label: "Receipt app", href: EN.receipts, desc: "Scan, enrich and find your receipts." },
      ],
    },
  },
];

export function getPost(slug: string): BlogPost | undefined {
  return posts.find((p) => p.slug === slug);
}
